import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Fade,
  Backdrop,
  Chip,
} from "@mui/material";

import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";

import * as XLSX from "xlsx";

const ExcelUploadModal = ({
  open,
  onClose,
  selectedSurvey,
  onUploadSuccess,
  handleSnackbarOpen,
}) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setParsedData([]);
      setValidationErrors([]);
      setPreviewMode(false);
    }
  }, [open]);
  const normalizeYesNo = (value) => {
    if (
      value === null ||
      value === undefined ||
      value.toString().trim() === ""
    ) {
      return "NO";
    }

    const normalized = value.toString().trim().toUpperCase();

    if (normalized === "YES") {
      return "YES";
    }

    if (normalized === "NO") {
      return "NO";
    }

    return null;
  };

  const getAllowTypingColumnIndex = (headers, optionNumber) => {
    const possibleNames = [
      `Allow Typing Option ${optionNumber}`,
      `Allow Typing for Option ${optionNumber}`,
      `Allow Tyiping Option ${optionNumber}`,
      `Allow Tyiping for Option ${optionNumber}`,
      `AllowTypingOption${optionNumber}`,
      `AllowTyping${optionNumber}`,
    ];

    return headers.findIndex((header) => {
      if (!header) return false;

      const normalizedHeader = header
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      return possibleNames.some(
        (name) => normalizedHeader === name.toLowerCase().replace(/\s+/g, " "),
      );
    });
  };
  const getOptionColumnIndex = (headers, optionNumber) => {
    const possibleNames = [`Option ${optionNumber}`, `Option${optionNumber}`];

    return headers.findIndex((header) => {
      if (!header) return false;

      const normalizedHeader = header
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      return possibleNames.some(
        (name) => normalizedHeader === name.toLowerCase().replace(/\s+/g, " "),
      );
    });
  };
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];

    if (!uploadedFile) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (
      !allowedTypes.includes(uploadedFile.type) &&
      !uploadedFile.name.match(/\.(xlsx|xls|csv)$/i)
    ) {
      handleSnackbarOpen(
        "Please upload a valid Excel file (.xlsx, .xls) or CSV file",
        "error",
      );
      return;
    }

    setFile(uploadedFile);

    parseExcelFile(uploadedFile);
  };
  const parseExcelFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target.result;

        const workbook = XLSX.read(data, {
          type: "binary",
        });

        const sheetName = workbook.SheetNames[0];

        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });
        if (jsonData.length < 2) {
          handleSnackbarOpen(
            "Excel file must contain at least a header row and one data row",
            "error",
          );

          return;
        }

        const headers = jsonData[0].map((header) => header?.toString().trim());

        const dataRows = jsonData.slice(1);
        const questionColumnIndex = headers.findIndex(
          (header) => header?.toLowerCase() === "question",
        );

        const questionTypeColumnIndex = headers.findIndex(
          (header) => header?.toLowerCase() === "questiontype",
        );

        const canPickDateColumnIndex = headers.findIndex(
          (header) => header?.toLowerCase() === "canpickdate",
        );
        if (questionColumnIndex === -1) {
          handleSnackbarOpen(
            'Excel file must have a "Question" column',
            "error",
          );

          return;
        }
        if (questionTypeColumnIndex === -1) {
          handleSnackbarOpen(
            'Excel file must have a "QuestionType" column',
            "error",
          );

          return;
        }
        if (canPickDateColumnIndex === -1) {
          handleSnackbarOpen(
            'Excel file must have a "canPickDate" column',
            "error",
          );

          return;
        }

        const processedData = [];
        const errors = [];
        dataRows.forEach((row, index) => {
          const rowNumber = index + 2;
          if (
            !row ||
            row.every((cell) => !cell || cell.toString().trim() === "")
          ) {
            return;
          }
          const question = row[questionColumnIndex]?.toString().trim();

          if (!question) {
            errors.push(`Row ${rowNumber}: Question text is required`);

            return;
          }
          const rawQuestionType = row[questionTypeColumnIndex]
            ?.toString()
            .trim()
            .toUpperCase();

          if (!rawQuestionType) {
            errors.push(`Row ${rowNumber}: QuestionType is required`);

            return;
          }

          const allowedQuestionTypes = ["MULTIPLE CHOICE", "OPEN QUESTION"];

          if (!allowedQuestionTypes.includes(rawQuestionType)) {
            errors.push(
              `Row ${rowNumber}: Invalid QuestionType "${rawQuestionType}". Allowed values are MULTIPLE CHOICE or OPEN QUESTION`,
            );

            return;
          }
          const rawCanPickDate = row[canPickDateColumnIndex];
          const canPickDate = normalizeYesNo(rawCanPickDate);
          if (canPickDate === null) {
            errors.push(`Row ${rowNumber}: canPickDate must be YES or NO`);

            return;
          }
          const options = [];

          for (let optionNumber = 1; optionNumber <= 10; optionNumber++) {
            const optionColumnIndex = getOptionColumnIndex(
              headers,
              optionNumber,
            );
            if (optionColumnIndex === -1) {
              continue;
            }

            const optionValue = row[optionColumnIndex]?.toString().trim();
            if (!optionValue) {
              continue;
            }
            const allowTypingColumnIndex = getAllowTypingColumnIndex(
              headers,
              optionNumber,
            );

            let allowTypingOption = "NO";

            if (allowTypingColumnIndex !== -1) {
              const rawAllowTyping = row[allowTypingColumnIndex];

              allowTypingOption = normalizeYesNo(rawAllowTyping);

              if (allowTypingOption === null) {
                errors.push(
                  `Row ${rowNumber}: Allow Typing Option ${optionNumber} for "${optionValue}" must be YES or NO`,
                );

                return;
              }
            }

            options.push({
              optionNumber,
              optionText: optionValue,
              allowTypingOption,
            });
          }
          if (rawQuestionType === "MULTIPLE CHOICE") {
            if (options.length < 2) {
              errors.push(
                `Row ${rowNumber}: MULTIPLE CHOICE question must have at least 2 answer options`,
              );

              return;
            }

            if (options.length > 10) {
              errors.push(
                `Row ${rowNumber}: Maximum 10 answer options are allowed`,
              );

              return;
            }
          }
          processedData.push({
            rowNumber,

            question,

            questionType: rawQuestionType,

            canPickDate,

            options,

            isValid: true,
          });
        });

        if (processedData.length === 0) {
          handleSnackbarOpen(
            "No valid questions found in the Excel file",
            "error",
          );

          setParsedData([]);

          setValidationErrors(errors);

          setPreviewMode(true);

          return;
        }
        setParsedData(processedData);

        setValidationErrors(errors);

        setPreviewMode(true);
        if (errors.length === 0) {
          handleSnackbarOpen(
            `Successfully read ${processedData.length} questions`,
            "success",
          );
        } else {
          handleSnackbarOpen(
            `Found ${errors.length} validation errors. Please review.`,
            "warning",
          );
        }
      } catch (error) {
        console.error("Error parsing Excel file:", error);

        handleSnackbarOpen(
          "Error reading Excel file. Please check the file format.",
          "error",
        );
      }
    };

    reader.readAsBinaryString(file);
  };
  const handleUploadQuestions = async () => {
    if (
      !selectedSurvey ||
      parsedData.length === 0 ||
      validationErrors.length > 0
    ) {
      return;
    }

    setUploading(true);

    try {
      const promises = parsedData.map(async (item) => {
        const requestBody = {
          surveyId: selectedSurvey.id,
          questionText: item.question,
          questionType: item.questionType,
          canPickDate: item.canPickDate,

          isRequired: true,

          moduleId: selectedSurvey.moduleId || null,
          answerOptions: item.options.map((option, index) => ({
            optionText: option.optionText,
            allowTypingOption: option.allowTypingOption,

            isActive: true,

            displayOrder: index + 1,
          })),
        };

        console.log("Uploading survey question:", requestBody);

        const response = await fetch("/api/support/survey-question", {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          credentials: "include",

          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          let errorData = {};

          try {
            errorData = await response.json();
          } catch {
            errorData = {};
          }

          throw new Error(
            `Question "${item.question}": ${
              errorData.message || "Failed to create"
            }`,
          );
        }

        return await response.json();
      });
      const results = await Promise.allSettled(promises);

      const successful = results.filter((r) => r.status === "fulfilled");

      const failed = results.filter((r) => r.status === "rejected");
      if (successful.length > 0) {
        handleSnackbarOpen(
          `Successfully uploaded ${successful.length} questions!`,
          "success",
        );

        onUploadSuccess?.();
      }
      if (failed.length > 0) {
        console.error("Failed uploads:", failed);

        handleSnackbarOpen(
          `${failed.length} questions failed to upload.`,
          "error",
        );
      }
      if (failed.length === 0) {
        onClose();
      }
    } catch (error) {
      console.error("Upload error:", error);

      handleSnackbarOpen(
        "Failed to upload questions. Please try again.",
        "error",
      );
    } finally {
      setUploading(false);
    }
  };
  const downloadTemplate = () => {
    const headers = [
      "Question",
      "QuestionType",
      "canPickDate",

      "Option 1",
      "Allow Typing Option 1",

      "Option 2",
      "Allow Typing Option 2",

      "Option 3",
      "Allow Typing Option 3",

      "Option 4",
      "Allow Typing Option 4",

      "Option 5",
      "Allow Typing Option 5",

      "Option 6",
      "Allow Typing Option 6",

      "Option 7",
      "Allow Typing Option 7",

      "Option 8",
      "Allow Typing Option 8",

      "Option 9",
      "Allow Typing Option 9",

      "Option 10",
      "Allow Typing Option 10",
    ];

    const templateData = [
      headers,

      [
        "What is your favorite color?",
        "MULTIPLE CHOICE",
        "NO",

        "Red",
        "NO",

        "Blue",
        "NO",

        "Green",
        "NO",

        "Other",
        "YES",
      ],

      ["Please describe your experience.", "OPEN QUESTION", "NO"],

      ["When did you attend the examination?", "OPEN QUESTION", "YES"],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    ws["!cols"] = [
      { wch: 55 },
      { wch: 20 },
      { wch: 15 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },

      { wch: 25 },
      { wch: 24 },
    ];

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Survey Questions");

    XLSX.writeFile(wb, "survey_questions_template.xlsx");
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{
        backdropFilter: "blur(8px)",

        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",

            bgcolor: "white",

            borderRadius: 3,

            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",

            width: {
              xs: "95%",
              sm: "90%",
              md: "80%",
              lg: "70%",
            },

            maxWidth: "1000px",

            maxHeight: "90vh",

            overflow: "hidden",

            display: "flex",

            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              bgcolor: "primary.main",

              color: "white",

              p: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  fontWeight="700"
                  sx={{
                    mb: 0.5,
                  }}
                >
                  📊 Upload Survey Questions
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                  }}
                >
                  Upload questions using Excel template
                </Typography>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  color: "white",

                  backgroundColor: "rgba(255, 255, 255, 0.1)",

                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
            }}
          >
            {!previewMode ? (
              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    textAlign: "left",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    📋 Upload Instructions:
                  </Typography>

                  <Typography variant="body2" component="div">
                    • <strong>Question</strong> is required.
                    <br />• <strong>QuestionType</strong> is required and must
                    be MULTIPLE CHOICE or OPEN QUESTION.
                    <br />• <strong>canPickDate</strong> can be YES or NO. Empty
                    values are automatically treated as NO.
                    <br />
                    • MULTIPLE CHOICE questions must have at least 2 options.
                    <br />• Each option can have its own
                    <strong> Allow Typing Option</strong> value.
                    <br />
                    • Empty Allow Typing Option values are automatically treated
                    as NO.
                    <br />
                    • Empty canPickDate values are automatically treated as NO.
                    <br />
                    • OPEN QUESTION does not require answer options.
                    <br />• Empty rows will be skipped automatically.
                  </Typography>
                </Alert>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  sx={{
                    mb: 3,
                    mr: 2,
                  }}
                >
                  Download Template
                </Button>

                <Box
                  sx={{
                    border: "2px dashed #e0e0e0",

                    borderRadius: 2,

                    p: 4,

                    mb: 3,

                    "&:hover": {
                      borderColor: "#0484fc",
                    },

                    cursor: "pointer",
                  }}
                  onClick={() =>
                    document.getElementById("excel-upload").click()
                  }
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: "#0484fc",
                      mb: 2,
                    }}
                  />

                  <Typography variant="h6" gutterBottom>
                    {file ? file.name : "Click to upload Excel file"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Supported formats: .xlsx, .xls, .csv
                  </Typography>

                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{
                      display: "none",
                    }}
                    onChange={handleFileUpload}
                  />
                </Box>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="600">
                    📋 Questions Preview ({parsedData.length} questions)
                  </Typography>

                  <Button
                    variant="outlined"
                    onClick={() => setPreviewMode(false)}
                    size="small"
                  >
                    Upload Different File
                  </Button>
                </Box>

                {validationErrors.length > 0 && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="600"
                      gutterBottom
                    >
                      Validation Errors Found:
                    </Typography>

                    {validationErrors.map((error, index) => (
                      <Typography key={index} variant="body2">
                        • {error}
                      </Typography>
                    ))}
                  </Alert>
                )}

                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: 400,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Row</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Question</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Type</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Options</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {parsedData.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.rowNumber}</TableCell>

                          <TableCell
                            sx={{
                              maxWidth: 300,
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              title={item.question}
                            >
                              {item.question}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={item.questionType}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={item.canPickDate}
                              size="small"
                              color={
                                item.canPickDate === "YES"
                                  ? "primary"
                                  : "default"
                              }
                            />
                          </TableCell>

                          <TableCell>
                            {item.options.length === 0 ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No options
                              </Typography>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {item.options.map((option, idx) => (
                                  <Chip
                                    key={idx}
                                    label={
                                      option.allowTypingOption === "YES"
                                        ? `${option.optionText} ✎`
                                        : option.optionText
                                    }
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Valid"
                              color="success"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              borderTop: "1px solid #e0e0e0",

              p: 3,

              display: "flex",

              justifyContent: "space-between",

              alignItems: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {previewMode
                ? `Ready to upload ${parsedData.length} questions${
                    validationErrors.length > 0 ? " (Fix errors first)" : ""
                  }`
                : "Upload an Excel file with your survey questions"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>

              {previewMode && (
                <Button
                  variant="contained"
                  startIcon={
                    uploading ? <CircularProgress size={20} /> : <UploadIcon />
                  }
                  onClick={handleUploadQuestions}
                  disabled={
                    uploading ||
                    validationErrors.length > 0 ||
                    parsedData.length === 0
                  }
                  sx={{
                    background: "#006400",

                    "&:hover": {
                      background: "#9fe2bf",
                    },
                  }}
                >
                  {uploading ? "Uploading..." : "Upload Questions"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const UploadQuestionsButton = ({
  selectedSurvey,
  onUploadSuccess,
  handleSnackbarOpen,
}) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const handleUploadQuestions = () => {
    setUploadModalOpen(true);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        startIcon={<CloudUploadIcon />}
        onClick={handleUploadQuestions}
        fullWidth
        sx={{
          borderRadius: 2,

          py: 1.5,

          fontWeight: 600,

          textTransform: "none",

          borderWidth: 2,

          "&:hover": {
            borderWidth: 2,

            transform: "translateY(-1px)",

            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
          },

          transition: "all 0.2s ease-in-out",
        }}
      >
        Upload Questions
      </Button>

      <ExcelUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        selectedSurvey={selectedSurvey}
        onUploadSuccess={onUploadSuccess}
        handleSnackbarOpen={handleSnackbarOpen}
      />
    </>
  );
};
export default UploadQuestionsButton;
